import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Eye, Upload, Edit, Trash } from "lucide-react";
import { toast } from "sonner";

const departments = [
  { value: "legal_compliance", label: "Legal & Compliance" },
  { value: "finance", label: "Finance" },
  { value: "marketing", label: "Marketing" },
  { value: "operations", label: "Operations" },
  { value: "hr", label: "Human Resources" },
  { value: "clinical", label: "Clinical" },
];

export function StaffPermissionsManager() {
  const [staffAccess, setStaffAccess] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStaffAccess();
  }, []);

  const fetchStaffAccess = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("staff_department_access")
      .select(`
        *,
        user:user_id(email)
      `)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load staff access permissions");
      setIsLoading(false);
      return;
    }

    setStaffAccess(data || []);
    setIsLoading(false);
  };

  const getDepartmentBadge = (department: string) => {
    const colors: Record<string, string> = {
      legal_compliance: "bg-red-100 text-red-800",
      finance: "bg-green-100 text-green-800",
      marketing: "bg-purple-100 text-purple-800",
      operations: "bg-blue-100 text-blue-800",
      hr: "bg-yellow-100 text-yellow-800",
      clinical: "bg-pink-100 text-pink-800",
    };
    return (
      <Badge className={colors[department] || "bg-gray-100 text-gray-800"}>
        {department.replace("_", " ")}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Staff Access Management</CardTitle>
              <CardDescription>
                Grant department-specific access to staff members
              </CardDescription>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  Add Staff Member
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Staff Access</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Staff Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="staff@therappi.app" 
                    />
                  </div>

                  <div className="space-y-4">
                    <Label>Department Access</Label>
                    {departments.map((dept) => (
                      <Card key={dept.value} className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getDepartmentBadge(dept.value)}
                            <span className="font-medium">{dept.label}</span>
                          </div>
                          <Switch />
                        </div>
                        <div className="grid grid-cols-4 gap-2 ml-2">
                          <div className="flex items-center gap-2">
                            <Switch />
                            <Label className="text-sm flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              View
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch />
                            <Label className="text-sm flex items-center gap-1">
                              <Upload className="h-3 w-3" />
                              Upload
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch />
                            <Label className="text-sm flex items-center gap-1">
                              <Edit className="h-3 w-3" />
                              Edit
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch />
                            <Label className="text-sm flex items-center gap-1">
                              <Trash className="h-3 w-3" />
                              Delete
                            </Label>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  <Button className="w-full">Create Staff Access</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff Member</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Granted</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      Loading staff access...
                    </TableCell>
                  </TableRow>
                ) : staffAccess.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No staff access configured yet. Add your first staff member to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  staffAccess.map((access) => (
                    <TableRow key={access.id}>
                      <TableCell className="font-medium">
                        {access.user?.email || "Unknown User"}
                      </TableCell>
                      <TableCell>
                        {getDepartmentBadge(access.department)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {access.can_view && (
                            <Badge variant="secondary" className="gap-1">
                              <Eye className="h-3 w-3" />
                              View
                            </Badge>
                          )}
                          {access.can_upload && (
                            <Badge variant="secondary" className="gap-1">
                              <Upload className="h-3 w-3" />
                              Upload
                            </Badge>
                          )}
                          {access.can_edit && (
                            <Badge variant="secondary" className="gap-1">
                              <Edit className="h-3 w-3" />
                              Edit
                            </Badge>
                          )}
                          {access.can_delete && (
                            <Badge variant="secondary" className="gap-1">
                              <Trash className="h-3 w-3" />
                              Delete
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(access.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost">
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive">
                            Revoke
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
